/**
 * WebsiteAanvraagPage — Klant kan website laten bouwen (FASE 4).
 *
 * Toont:
 *  - Lijst van eigen aanvragen met status-timeline
 *  - Knop om nieuwe aanvraag in te dienen (modal met logo upload)
 *  - Per aanvraag: status, ETA, final URL als opgeleverd
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Globe, Plus, Upload, X, Loader2, CheckCircle, Clock, AlertCircle,
  RefreshCw, ArrowRight, Sparkles, FileText, ExternalLink,
} from 'lucide-react';

const STATUS_FLOW = ['aangevraagd', 'in_review', 'in_uitvoering', 'opgeleverd', 'gepubliceerd'];
const STATUS_LABELS = {
  aangevraagd: 'Aangevraagd',
  in_review: 'In review',
  in_uitvoering: 'In uitvoering',
  opgeleverd: 'Opgeleverd',
  gepubliceerd: 'Gepubliceerd',
  afgewezen: 'Afgewezen',
};
const STATUS_COLORS = {
  aangevraagd: { c: '#1d4ed8', bg: '#dbeafe' },
  in_review: { c: '#7c3aed', bg: '#f5f3ff' },
  in_uitvoering: { c: '#D4A017', bg: '#fef3c7' },
  opgeleverd: { c: '#059669', bg: '#d1fae5' },
  gepubliceerd: { c: '#0f172a', bg: '#e0e7ff' },
  afgewezen: { c: '#dc2626', bg: '#fee2e2' },
};

export default function WebsiteAanvraagPage() {
  const { api, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', '/api/platform/website-requests');
      setRequests(r.data.requests || []);
    } catch {
      toast.error('Laden mislukt');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="website-aanvraag-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <p style={{ fontSize: 11, color: '#D4A017', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Mijn website-aanvragen</p>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 26, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
            Laat uw website door ons bouwen
          </h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            Vraag een nieuwe website, redesign of landingspagina aan. Ons team neemt direct contact op.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="open-create-request" style={{ background: '#D4A017', color: '#0f172a', fontWeight: 700, padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <Plus size={14} /> Nieuwe aanvraag
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Loader2 className="animate-spin" /></div>
      ) : requests.length === 0 ? (
        <div data-testid="no-requests" style={{ background: 'white', border: '1px dashed #e2e8f0', borderRadius: 14, padding: '40px 24px', textAlign: 'center' }}>
          <Globe size={36} style={{ color: '#94a3b8', margin: '0 auto 12px' }} />
          <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Nog geen aanvragen</h2>
          <p style={{ color: '#64748b', fontSize: 13, margin: '6px 0 14px' }}>Vraag uw eerste website-bouw of redesign aan.</p>
          <button onClick={() => setShowCreate(true)} style={{ background: '#0f172a', color: 'white', padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            Start aanvraag
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {requests.map((r) => <RequestCard key={r.id} req={r} />)}
        </div>
      )}

      {showCreate && <CreateRequestModal api={api} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}


function RequestCard({ req }) {
  const colors = STATUS_COLORS[req.status] || STATUS_COLORS.aangevraagd;
  const currentStep = STATUS_FLOW.indexOf(req.status);
  return (
    <div data-testid={`request-card-${req.id}`} style={{ background: 'white', borderRadius: 14, padding: '20px 22px', border: '1px solid #f1f5f9' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <p style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{req.type_website || 'Nieuw'}</p>
          <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: 18, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
            {req.domein_wens}
          </h3>
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            Aangevraagd op {new Date(req.created_at).toLocaleDateString('nl-NL')} · {req.aantal_paginas} pagina's
          </p>
          {req.branche && <p style={{ fontSize: 12, color: '#64748b' }}>Branche: {req.branche}</p>}
        </div>
        <div>
          <span data-testid={`request-status-${req.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 12, color: colors.c, background: colors.bg }}>
            {STATUS_LABELS[req.status] || req.status}
          </span>
        </div>
      </div>

      {req.status !== 'afgewezen' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '10px 0', borderTop: '1px solid #f8fafc', borderBottom: '1px solid #f8fafc' }}>
          {STATUS_FLOW.map((s, i) => {
            const done = i <= currentStep;
            return (
              <React.Fragment key={s}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flex: 1, minWidth: 60 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: done ? '#D4A017' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {done ? <CheckCircle size={12} color="#0f172a" /> : <Clock size={11} color="#94a3b8" />}
                  </div>
                  <p style={{ fontSize: 9, color: done ? '#0f172a' : '#94a3b8', fontWeight: done ? 700 : 500, textAlign: 'center' }}>{STATUS_LABELS[s]}</p>
                </div>
                {i < STATUS_FLOW.length - 1 && (
                  <div style={{ flex: '0 0 18px', height: 2, background: i < currentStep ? '#D4A017' : '#e2e8f0' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {req.estimated_delivery && (
        <p style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
          <Clock size={11} style={{ display: 'inline', marginRight: 4 }} /> Verwachte oplevering: {req.estimated_delivery}
        </p>
      )}
      {req.final_url && req.status === 'gepubliceerd' && (
        <a href={req.final_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, background: '#0f172a', color: 'white', padding: '6px 12px', borderRadius: 8, textDecoration: 'none', fontSize: 12, fontWeight: 700 }}>
          Bekijk uw website <ExternalLink size={11} />
        </a>
      )}
      {req.afwijzing_reden && (
        <div style={{ marginTop: 10, background: '#fef2f2', borderLeft: '3px solid #f87171', padding: '8px 12px', borderRadius: '0 6px 6px 0' }}>
          <p style={{ fontSize: 11, color: '#b91c1c', fontWeight: 700 }}>Reden afwijzing:</p>
          <p style={{ fontSize: 12, color: '#0f172a' }}>{req.afwijzing_reden}</p>
        </div>
      )}
    </div>
  );
}


function CreateRequestModal({ api, onClose, onCreated }) {
  const [form, setForm] = useState({
    domein_wens: '', branche: '', bedrijfsnaam: '', huidige_url: '',
    type_website: 'nieuw', aantal_paginas: 5, extra_wensen: '',
    kleurvoorkeur: '', voorbeelden: '',
  });
  const [logoId, setLogoId] = useState(null);
  const [logoName, setLogoName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bestand te groot (max 5MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api('post', '/api/platform/website-requests/upload-logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoId(r.data.file_id);
      setLogoName(file.name);
      toast.success('Logo geüpload');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload mislukt');
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.domein_wens || form.domein_wens.length < 3) {
      toast.error('Vul een gewenst domein in');
      return;
    }
    setSaving(true);
    try {
      await api('post', '/api/platform/website-requests', { ...form, logo_file_id: logoId });
      toast.success('Aanvraag verzonden — wij nemen binnen 72u contact op');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verzenden mislukt');
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflow: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="create-request-modal" style={{ background: 'white', borderRadius: 16, padding: 24, maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Website-aanvraag</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#94a3b8" /></button>
        </div>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Gewenst domein *" required>
            <input data-testid="req-domein" required value={form.domein_wens} onChange={(e) => setForm({ ...form, domein_wens: e.target.value })} placeholder="bijvoorbedrijf.nl" style={inputStyle} />
          </Field>

          <Field label="Type aanvraag">
            <select data-testid="req-type" value={form.type_website} onChange={(e) => setForm({ ...form, type_website: e.target.value })} style={inputStyle}>
              <option value="nieuw">Nieuwe website</option>
              <option value="redesign">Redesign bestaande site</option>
              <option value="landing_page">Landingspagina</option>
            </select>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Bedrijfsnaam">
              <input value={form.bedrijfsnaam} onChange={(e) => setForm({ ...form, bedrijfsnaam: e.target.value })} style={inputStyle} />
            </Field>
            <Field label="Branche">
              <input value={form.branche} onChange={(e) => setForm({ ...form, branche: e.target.value })} placeholder="bv. bouw, horeca" style={inputStyle} />
            </Field>
          </div>

          {form.type_website === 'redesign' && (
            <Field label="Huidige URL">
              <input value={form.huidige_url} onChange={(e) => setForm({ ...form, huidige_url: e.target.value })} placeholder="https://huidigesite.nl" style={inputStyle} />
            </Field>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Aantal pagina's">
              <input type="number" min={1} max={50} value={form.aantal_paginas} onChange={(e) => setForm({ ...form, aantal_paginas: Number(e.target.value) })} style={inputStyle} />
            </Field>
            <Field label="Kleurvoorkeur">
              <input value={form.kleurvoorkeur} onChange={(e) => setForm({ ...form, kleurvoorkeur: e.target.value })} placeholder="bv. blauw/wit" style={inputStyle} />
            </Field>
          </div>

          <Field label="Logo (optioneel)">
            {logoId ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f0fdf4', padding: '8px 12px', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <p style={{ fontSize: 12, color: '#065f46', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckCircle size={14} /> {logoName}
                </p>
                <button type="button" onClick={() => { setLogoId(null); setLogoName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 11 }}>Verwijder</button>
              </div>
            ) : (
              <label data-testid="logo-upload-label" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', border: '1px dashed #cbd5e1', borderRadius: 8, cursor: 'pointer', background: '#f8fafc' }}>
                <Upload size={14} color="#64748b" />
                <span style={{ fontSize: 12, color: '#64748b' }}>{uploading ? 'Uploaden…' : 'Klik om logo te uploaden (PNG/JPG/SVG/WEBP — max 5MB)'}</span>
                <input data-testid="logo-input" type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={handleLogo} disabled={uploading} style={{ display: 'none' }} />
              </label>
            )}
          </Field>

          <Field label="Voorbeelden / inspiratie URLs">
            <textarea rows={2} value={form.voorbeelden} onChange={(e) => setForm({ ...form, voorbeelden: e.target.value })} placeholder="Eén URL per regel" style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>

          <Field label="Extra wensen">
            <textarea rows={3} value={form.extra_wensen} onChange={(e) => setForm({ ...form, extra_wensen: e.target.value })} placeholder="Welke pagina's? Welke features? Online betalen? Boekingen?" style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>

          <button type="submit" disabled={saving} data-testid="submit-request" style={{ background: '#D4A017', color: '#0f172a', fontWeight: 800, padding: '12px 16px', borderRadius: 10, border: 'none', cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: saving ? 0.6 : 1 }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {saving ? 'Versturen…' : 'Verstuur aanvraag'}
          </button>
          <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>Wij nemen binnen 72 uur contact op om de aanvraag te bespreken.</p>
        </form>
      </div>
    </div>
  );
}


const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };

function Field({ label, required, children }) {
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'block' }}>{label}{required && <span style={{ color: '#dc2626' }}> *</span>}</label>
      {children}
    </div>
  );
}
